import {
  Button,
  DatePicker,
  defaultErrorHandling,
  EchartsWithTheme,
  Radio,
  Wrapper,
  useBreadcrumbRoutes,
  useTheme,
  Empty,
} from '@maxtropy/components';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';
import { Space, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import qs from 'qs';
import ResizeObserver from 'rc-resize-observer';
import type { CSSProperties, MutableRefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  apiV2BottledGasStatisticsByDepartmentPost,
  apiV2BottledGasStatisticsByMediumPost,
  apiV2BottledGasStatisticsDepartmentTrendPost,
  V2BottledGasStatisticsByDepartmentPostResponse,
  V2BottledGasStatisticsByMediumPostResponse,
  V2BottledGasStatisticsDepartmentTrendPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import titleIcon from '@/assets/images/title_icon.png';
import styles from './index.module.scss';
import OutboundDetailsModal from './components/OutboundDetailsModal';

const timeline = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

const gasTypeHighlightColors = [
  '#52E7FF',
  '#FF477B',
  '#FFCB47',
  '#6F47FF',
  '#16DD8E',
  '#2D8DFF',
  '#ABD335',
  '#234ED9',
  '#FF9247',
  '#854ECA',
  '#4A90E2',
];

const hexToRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '');
  const num = Number.parseInt(normalized, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r},${g},${b},${alpha})`;
};

const buildGasTypeColorMap = (types: string[]) => {
  const map = new Map<string, string>();
  types.forEach((name, index) => {
    map.set(name, gasTypeHighlightColors[index % gasTypeHighlightColors.length]);
  });
  return map;
};

const MIN_TREND_CHART_HEIGHT = 320;
const RANKING_GRID_LEFT = 50;
const RANKING_BAR_WIDTH = '24%';
const RANKING_HIT_AREA_WIDTH = '52%';
const RANKING_AXIS_LINE_STYLE = { color: 'rgba(255,255,255,0.65)' };
const RANKING_AXIS_LABEL_COLOR = 'rgba(255,255,255,0.85)';
const RANKING_ACTIVE_AXIS_LABEL_COLOR = '#52E7FF';
const DETAIL_BAR_MAX_WIDTH = 24;

const getRankingBandHeight = (api: any) => {
  const categoryHeight = api.size([0, 1])?.[1] ?? 0;
  return Math.min(Math.max(categoryHeight * 0.52, 28), 64);
};

const createRankingYAxisLineSeries = (itemCount: number) => ({
  type: 'custom',
  silent: true,
  data: itemCount > 0 ? [0] : [],
  z: 20,
  zlevel: 11,
  renderItem: (_params: any, api: any) => {
    if (!itemCount) return null;

    const x = api.coord([0, 0])[0];
    const categoryHeight = api.size([0, 1])?.[1] ?? 0;
    const firstY = api.coord([0, 0])[1];
    const lastY = api.coord([0, itemCount - 1])[1];

    return {
      type: 'line',
      shape: {
        x1: x,
        y1: Math.min(firstY, lastY) - categoryHeight / 2,
        x2: x,
        y2: Math.max(firstY, lastY) + categoryHeight / 2,
      },
      style: {
        stroke: RANKING_AXIS_LINE_STYLE.color,
        lineWidth: 1,
      },
    };
  },
});

const chartEmptyNode = (
  <div
    style={{
      height: '100%',
      minHeight: MIN_TREND_CHART_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Empty />
  </div>
);

const trendChartStyle: CSSProperties = { height: '100%', minHeight: MIN_TREND_CHART_HEIGHT, width: '100%' };

const resizeTrendChart = (
  chartRef: MutableRefObject<EchartsWithThemeRef | undefined>,
  width: number,
  height = MIN_TREND_CHART_HEIGHT
) => {
  chartRef.current?.getInstance()?.resize({
    width: Math.max(Math.round(width), 0),
    height: Math.max(Math.round(height), MIN_TREND_CHART_HEIGHT),
  });
};

const BottledGasStatistics = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const theme = useTheme();
  const [tab, setTab] = useState<number>(1);
  const [selectedAreaIndex, setSelectedAreaIndex] = useState<number>(0);
  const [hoverAreaIndex, setHoverAreaIndex] = useState<number | null>(null);
  const [selectedGasTypeIndex, setSelectedGasTypeIndex] = useState<number>(0);
  const [hoverGasTypeIndex, setHoverGasTypeIndex] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(31, 'day'), dayjs().endOf('day')]);
  const [calendarRange, setCalendarRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [loading, setLoading] = useState(false);
  const [departmentDetailLoading, setDepartmentDetailLoading] = useState(false);
  const [gasMediumDetailLoading, setGasMediumDetailLoading] = useState(false);
  const [outboundVisible, setOutboundVisible] = useState(false);
  const [trendData, setTrendData] = useState<V2BottledGasStatisticsDepartmentTrendPostResponse>();
  const [departmentDetailData, setDepartmentDetailData] = useState<V2BottledGasStatisticsByDepartmentPostResponse>();
  const [gasMediumDetailData, setGasMediumDetailData] = useState<V2BottledGasStatisticsByMediumPostResponse>();
  const areaChartRef = useRef<EchartsWithThemeRef>();
  const departmentDetailChartRef = useRef<EchartsWithThemeRef>();
  const gasTypeChartRef = useRef<EchartsWithThemeRef>();
  const gasMediumDetailChartRef = useRef<EchartsWithThemeRef>();

  const areaNames = useMemo(() => trendData?.departmentGasData?.map(d => d.reqDepartmentName ?? '') ?? [], [trendData]);
  const areaValues = useMemo(() => trendData?.departmentGasData?.map(d => Number(d.num ?? 0)) ?? [], [trendData]);
  const gasTypes = useMemo(() => trendData?.gasTypeData?.map(d => d.gasTypeName ?? '') ?? [], [trendData]);
  const gasTypeValues = useMemo(() => trendData?.gasTypeData?.map(d => Number(d.num ?? 0)) ?? [], [trendData]);
  const selectedDepartment = useMemo(
    () => trendData?.departmentGasData?.[selectedAreaIndex],
    [trendData, selectedAreaIndex]
  );
  const selectedGasType = useMemo(
    () => trendData?.gasTypeData?.[selectedGasTypeIndex],
    [trendData, selectedGasTypeIndex]
  );
  const hasAreaData = areaNames.length > 0 && areaValues.length > 0;
  const hasGasTypeData = gasTypes.length > 0 && gasTypeValues.length > 0;
  const hasDepartmentDetailData = (departmentDetailData?.departmentGasDetails?.length ?? 0) > 0;
  const hasGasMediumDetailData = (gasMediumDetailData?.gasDetails?.length ?? 0) > 0;
  const gasTypeColorMap = useMemo(() => buildGasTypeColorMap(gasTypes), [gasTypes]);
  const requestRange = useMemo<[Dayjs, Dayjs]>(() => {
    if (tab === 1) {
      const start = dateRange[0].startOf('day');
      const end = dateRange[1].endOf('day');
      const maxStart = end.subtract(6, 'month').add(1, 'day').startOf('day');
      return [start.isBefore(maxStart) ? maxStart : start, end];
    }
    return [dateRange[0].startOf('month'), dateRange[1].endOf('month')];
  }, [tab, dateRange]);

  const fetchTrendData = async () => {
    setLoading(true);
    apiV2BottledGasStatisticsDepartmentTrendPost({
      startTime: requestRange[0].valueOf(),
      endTime: requestRange[1].valueOf(),
      timeResolution: tab === 1 ? 'DAY' : 'MONTH',
    })
      .onError(e => {
        defaultErrorHandling(e);
        return true;
      })
      .then(res => {
        setTrendData(res);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTrendData();
  }, [tab, requestRange]);

  useEffect(() => {
    if (selectedAreaIndex > Math.max(areaNames.length - 1, 0)) {
      setSelectedAreaIndex(0);
    }
  }, [areaNames.length, selectedAreaIndex]);

  useEffect(() => {
    if (!selectedDepartment?.reqDepartment) {
      setDepartmentDetailData(undefined);
      setDepartmentDetailLoading(false);
      return;
    }
    setDepartmentDetailLoading(true);
    apiV2BottledGasStatisticsByDepartmentPost({
      reqDepartment: selectedDepartment.reqDepartment,
      startTime: requestRange[0].valueOf(),
      endTime: requestRange[1].valueOf(),
      timeResolution: tab === 1 ? 'DAY' : 'MONTH',
    })
      .onError(e => {
        defaultErrorHandling(e);
        return true;
      })
      .then(res => {
        setDepartmentDetailData(res);
      })
      .finally(() => setDepartmentDetailLoading(false));
  }, [selectedDepartment?.reqDepartment, requestRange, tab]);

  useEffect(() => {
    if (selectedGasTypeIndex > Math.max(gasTypes.length - 1, 0)) {
      setSelectedGasTypeIndex(0);
    }
  }, [gasTypes.length, selectedGasTypeIndex]);

  useEffect(() => {
    if (!selectedGasType?.gasType) {
      setGasMediumDetailData(undefined);
      setGasMediumDetailLoading(false);
      return;
    }
    setGasMediumDetailLoading(true);
    apiV2BottledGasStatisticsByMediumPost({
      gasType: selectedGasType.gasType,
      startTime: requestRange[0].valueOf(),
      endTime: requestRange[1].valueOf(),
      timeResolution: tab === 1 ? 'DAY' : 'MONTH',
    })
      .onError(e => {
        defaultErrorHandling(e);
        return true;
      })
      .then(res => {
        setGasMediumDetailData(res);
      })
      .finally(() => setGasMediumDetailLoading(false));
  }, [selectedGasType?.gasType, requestRange, tab]);

  const disabledDate = (current: Dayjs) => {
    const [start, end] = calendarRange;
    if (tab === 1) {
      const tooEarly = current && current > dayjs().endOf('day');
      if (!start && !end) {
        return !!tooEarly;
      }
      const anchor = start ?? end;
      const tooLate = !!anchor && current.diff(anchor, 'month', true) > 6;
      const tooOld = !!anchor && anchor.diff(current, 'month', true) > 6;
      return !!tooEarly || !!tooLate || !!tooOld;
    }

    const maxMonths = 36;
    const monthCurrent = current.startOf('month');
    if (start && !end) {
      const anchor = start.startOf('month');
      return (
        monthCurrent.isBefore(anchor.subtract(maxMonths - 1, 'month'), 'month') ||
        monthCurrent.isAfter(anchor.add(maxMonths - 1, 'month'), 'month')
      );
    }
    if (!start && end) {
      const anchor = end.startOf('month');
      return (
        monthCurrent.isBefore(anchor.subtract(maxMonths - 1, 'month'), 'month') ||
        monthCurrent.isAfter(anchor.add(maxMonths - 1, 'month'), 'month')
      );
    }
    return false;
  };

  const singleGasOption = useMemo(() => {
    // loading 期间返回空配置，避免颜色变化时的多余绘制
    if (gasMediumDetailLoading) {
      return {
        backgroundColor: 'rgba(74,144,226,0.1)',
        title: {
          text: `${selectedGasType?.gasTypeName ?? '气体'}-瓶装气体详情`,
          left: 16,
          top: 16,
        },
        grid: { top: 80, right: 24, bottom: 16, left: 20 },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value', name: '瓶' },
        series: [],
      };
    }
    const list = gasMediumDetailData?.gasDetails ?? [];
    const axisTimes = Array.from(new Set(list.map(item => item.time).filter(Boolean) as string[])).sort();
    const dataValues = axisTimes.map(time => {
      const item = list.find(detail => detail.time === time);
      return Number(item?.num ?? 0);
    });
    const selectedColor = gasTypeHighlightColors[selectedGasTypeIndex % gasTypeHighlightColors.length];

    return {
      backgroundColor: 'rgba(74,144,226,0.1)',
      title: {
        text: `${selectedGasType?.gasTypeName ?? '气体'}-瓶装气体详情`,
        left: 16,
        top: 16,
      },
      tooltip: { trigger: 'axis' },
      grid: { top: 80, right: 24, bottom: 16, left: 44, containLabel: true },
      legend: {
        top: 16,
        right: 16,
        itemHeight: 12,
        itemWidth: 12,
        icon: 'rect',
      },
      xAxis: {
        type: 'category',
        data: axisTimes,
        axisTick: { show: false },
        axisLabel: {
          formatter: (value: string) => (tab === 1 ? dayjs(value).format('MM-DD') : dayjs(value).format('YYYY-MM')),
        },
      },
      yAxis: {
        type: 'value',
        name: '瓶',
        nameLocation: 'end',
        nameGap: 6,
        nameTextStyle: {
          align: 'left',
          padding: [0, 0, 0, 0],
        },
        axisLabel: {
          margin: 12,
        },
      },
      series: [
        {
          name: selectedGasType?.gasTypeName ?? '气体',
          type: 'bar',
          barMaxWidth: DETAIL_BAR_MAX_WIDTH,
          data: dataValues,
          itemStyle: { color: selectedColor },
        },
      ],
    };
  }, [gasMediumDetailLoading, gasMediumDetailData, selectedGasType?.gasTypeName, selectedGasTypeIndex, tab]);

  const stackedOption = useMemo(() => {
    const list = departmentDetailData?.departmentGasDetails ?? [];
    const axisTimes = Array.from(new Set(list.map(item => item.time).filter(Boolean) as string[])).sort();
    const gasTypeList = Array.from(new Set(list.map(item => item.gasTypeName).filter(Boolean) as string[]));
    const detailMap = new Map<string, number>();
    list.forEach(item => {
      if (!item.gasTypeName || !item.time) return;
      detailMap.set(`${item.gasTypeName}_${item.time}`, Number(item.num ?? 0));
    });

    return {
      backgroundColor: 'rgba(74,144,226,0.1)',
      title: {
        text: `${selectedDepartment?.reqDepartmentName ?? '部门'}-瓶装气体详情`,
        left: 16,
        top: 16,
        textStyle: { color: 'rgba(255,255,255,0.85)' },
      },
      tooltip: { trigger: 'axis' },
      legend: {
        top: 16,
        right: 16,
        itemHeight: 12,
        itemWidth: 12,
        icon: 'rect',
      },
      grid: { top: 80, right: 24, bottom: 16, left: 30, containLabel: true },
      xAxis: {
        type: 'category',
        data: axisTimes,
        axisTick: { show: false },
        axisLabel: {
          formatter: (value: string) => (tab === 1 ? dayjs(value).format('MM-DD') : dayjs(value).format('YYYY-MM')),
          hideOverlap: true,
        },
      },
      yAxis: {
        type: 'value',
        name: '瓶',
        nameLocation: 'end',
        nameGap: 6,
        nameTextStyle: {
          align: 'left',
          padding: [0, 0, 0, 0],
        },
        axisLabel: {
          margin: 12,
        },
      },
      series: gasTypeList.map((name, index) => ({
        name,
        type: 'bar',
        stack: '总量',
        barMaxWidth: DETAIL_BAR_MAX_WIDTH,
        data: axisTimes.map(time => detailMap.get(`${name}_${time}`) ?? 0),
        itemStyle: {
          color: gasTypeColorMap.get(name) ?? gasTypeHighlightColors[index % gasTypeHighlightColors.length],
        },
      })),
    };
  }, [departmentDetailData, selectedDepartment?.reqDepartmentName, tab, gasTypeColorMap]);

  const areaOption = useMemo(() => {
    const maxValue = Math.max(Math.max(...areaValues, 0) + 50, 50);
    return {
      tooltip: {
        show: false,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      grid: { top: 10, right: 40, bottom: 10, left: RANKING_GRID_LEFT },
      xAxis: {
        type: 'value',
        max: maxValue,
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(255,255,255,0.3)',
          },
        },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: areaNames,
        triggerEvent: true,
        axisLabel: {
          fontSize: 14,
          margin: 12,
          color: (_: string, index: number) =>
            index === selectedAreaIndex ? RANKING_ACTIVE_AXIS_LABEL_COLOR : RANKING_AXIS_LABEL_COLOR,
          fontWeight: 400,
        },
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          show: true,
          lineStyle: RANKING_AXIS_LINE_STYLE,
        },
      },
      series: [
        {
          type: 'custom',
          silent: true,
          data: areaNames.map((_, index) => index),
          z: 0,
          renderItem: (params: any, api: any) => {
            const index = api.value(0) as number;
            const isHover = hoverAreaIndex !== null && hoverAreaIndex !== selectedAreaIndex && index === hoverAreaIndex;
            const isSelected = index === selectedAreaIndex;
            if (!isHover && !isSelected) return null;

            const yCoord = api.coord([0, index])[1];
            const start = api.coord([0, index])[0];
            const end = api.coord([maxValue, index])[0];
            const bandHeight = getRankingBandHeight(api);

            return {
              type: 'rect',
              shape: {
                x: start,
                y: yCoord - bandHeight / 2,
                width: end - start,
                height: bandHeight,
              },
              style: {
                fill: isHover ? 'rgba(255,255,255,0.1)' : 'rgba(82,231,255,0.2)',
              },
            };
          },
        },
        {
          name: '__row_hit_area__',
          type: 'bar',
          data: areaNames.map(() => maxValue),
          barWidth: RANKING_HIT_AREA_WIDTH,
          barGap: '-100%',
          itemStyle: { color: 'rgba(255,255,255,0.001)' },
          emphasis: { itemStyle: { color: 'rgba(255,255,255,0.001)' } },
          z: 2,
        },
        {
          type: 'bar',
          data: areaValues.map((value, index) => ({
            value,
            itemStyle: {
              color:
                index === selectedAreaIndex
                  ? '#00ADFF'
                  : hoverAreaIndex !== null && index === hoverAreaIndex
                  ? 'rgba(0,173,255,0.8)'
                  : 'rgba(0,173,255,0.4)',
            },
            label: {
              color: index === selectedAreaIndex ? RANKING_ACTIVE_AXIS_LABEL_COLOR : RANKING_AXIS_LABEL_COLOR,
            },
          })),
          barWidth: RANKING_BAR_WIDTH,
          barGap: '-100%',
          barCategoryGap: '50%',
          label: {
            show: true,
            position: 'right',

            color: 'rgba(255,255,255,0.85)',
          },
          z: 10,
          zlevel: 10,
        },
        createRankingYAxisLineSeries(areaNames.length),
      ],
    };
  }, [areaNames, areaValues, selectedAreaIndex, hoverAreaIndex]);

  const gasTypeOption = useMemo(() => {
    const maxValue = Math.max(Math.max(...gasTypeValues, 0) + 50, 50);
    return {
      grid: { top: 10, right: 40, bottom: 10, left: RANKING_GRID_LEFT },
      xAxis: {
        type: 'value',
        max: maxValue,
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(255,255,255,0.3)',
          },
        },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: 'category',
        data: gasTypes,
        inverse: true,
        triggerEvent: true,
        axisLabel: {
          fontSize: 14,
          margin: 12,
          color: (_: string, index: number) =>
            index === selectedGasTypeIndex ? RANKING_ACTIVE_AXIS_LABEL_COLOR : RANKING_AXIS_LABEL_COLOR,
          fontWeight: 400,
        },
        splitLine: {
          show: false,
        },
        axisTick: { show: false },
        axisLine: {
          show: true,
          lineStyle: RANKING_AXIS_LINE_STYLE,
        },
      },
      tooltip: {
        show: false,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      series: [
        {
          type: 'custom',
          silent: true,
          data: gasTypes.map((_, index) => index),
          z: 0,
          renderItem: (_params: any, api: any) => {
            const index = api.value(0) as number;
            const isHover =
              hoverGasTypeIndex !== null && hoverGasTypeIndex !== selectedGasTypeIndex && index === hoverGasTypeIndex;
            const isSelected = index === selectedGasTypeIndex;
            if (!isHover && !isSelected) return null;

            const yCoord = api.coord([0, index])[1];
            const start = api.coord([0, index])[0];
            const end = api.coord([maxValue, index])[0];
            const bandHeight = getRankingBandHeight(api);

            return {
              type: 'rect',
              shape: {
                x: start,
                y: yCoord - bandHeight / 2,
                width: end - start,
                height: bandHeight,
              },
              style: {
                fill: isHover ? 'rgba(255,255,255,0.1)' : 'rgba(82,231,255,0.2)',
              },
            };
          },
        },
        {
          name: '__row_hit_area__',
          type: 'bar',
          data: gasTypes.map(() => maxValue),
          barWidth: RANKING_HIT_AREA_WIDTH,
          barGap: '-100%',
          itemStyle: { color: 'rgba(255,255,255,0.001)' },
          emphasis: { itemStyle: { color: 'rgba(255,255,255,0.001)' } },
          z: 2,
        },
        {
          type: 'bar',
          data: gasTypeValues.map((value, index) => {
            const baseColor = gasTypeHighlightColors[index % gasTypeHighlightColors.length];
            const isSelected = index === selectedGasTypeIndex;
            const isHover = hoverGasTypeIndex !== null && index === hoverGasTypeIndex && !isSelected;
            return {
              value,
              itemStyle: {
                color: isSelected ? baseColor : isHover ? hexToRgba(baseColor, 0.8) : hexToRgba(baseColor, 0.4),
              },
              label: {
                color: isSelected ? '#52E7FF' : 'rgba(255,255,255,0.85)',
              },
            };
          }),
          barWidth: RANKING_BAR_WIDTH,
          barGap: '-100%',
          barCategoryGap: '50%',
          label: { show: true, position: 'right', color: 'rgba(255,255,255,0.85)' },
          z: 10,
          zlevel: 10,
        },
        createRankingYAxisLineSeries(gasTypes.length),
      ],
    };
  }, [gasTypes, gasTypeValues, hoverGasTypeIndex, selectedGasTypeIndex]);

  const handleExport = () => {
    window.open(
      `/api/v2/bottled/gas/export?${qs.stringify({
        startTime: requestRange[0].valueOf(),
        endTime: requestRange[1].valueOf(),
      })}`
    );
  };

  const filters = (
    <div className={styles.header}>
      <div className={styles.left}>
        <Radio.Group
          value={tab}
          onChange={e => {
            const nextTab = e.target.value as number;
            setTab(nextTab);
            if (nextTab === 1) {
              setDateRange([dayjs().subtract(31, 'day').startOf('day'), dayjs().endOf('day')]);
            } else {
              setDateRange([dayjs().subtract(5, 'month').startOf('month'), dayjs().endOf('month')]);
            }
            setCalendarRange([null, null]);
          }}
          buttonStyle="solid"
        >
          <Radio.Button value={1}>按天</Radio.Button>
          <Radio.Button value={2}>按月</Radio.Button>
        </Radio.Group>
        <DatePicker.RangePicker
          picker={tab === 1 ? 'date' : 'month'}
          format={tab === 1 ? 'YYYY-MM-DD' : 'YYYY-MM'}
          value={dateRange}
          disabledDate={disabledDate}
          onCalendarChange={vals => setCalendarRange([vals?.[0] ?? null, vals?.[1] ?? null])}
          onChange={val => {
            if (val?.[0] && val?.[1]) {
              if (tab === 1 && val[1].diff(val[0], 'month', true) > 6) {
                return;
              }
              const nextRange: [Dayjs, Dayjs] =
                tab === 1
                  ? [val[0].startOf('day'), val[1].endOf('day')]
                  : [val[0].startOf('month'), val[1].endOf('month')];
              setDateRange(nextRange);
            }
            setCalendarRange([null, null]);
          }}
        />
      </div>
      <Space size={8}>
        <Button
          type="primary"
          onClick={() => {
            setOutboundVisible(true);
          }}
        >
          瓶装气体出库明细
        </Button>
        <Button type="primary" onClick={handleExport}>
          导出
        </Button>
      </Space>
    </div>
  );

  return (
    <div className={styles.customWrapper}>
      <Wrapper routes={breadcrumbRoutes?.routes} className={styles.wrapper} hasPadding={false} filters={filters}>
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <img src={titleIcon} alt="" />
            <span>各部门瓶装气体出库趋势</span>
          </div>
          <div className={styles.grid}>
            <div className={styles.card}>
              <Spin spinning={loading}>
                <ResizeObserver
                  onResize={({ width, height }) => {
                    resizeTrendChart(areaChartRef, width, height);
                  }}
                >
                  <EchartsWithTheme
                    ref={e => {
                      if (e) areaChartRef.current = e;
                    }}
                    option={areaOption}
                    style={trendChartStyle}
                    theme={theme}
                    empty={{
                      isShowEmpty: !hasAreaData,
                      emptyNode: chartEmptyNode,
                    }}
                    onEvents={{
                      click: (params: { dataIndex?: number; componentType?: string; value?: string }) => {
                        if (params.componentType === 'series' && typeof params.dataIndex === 'number') {
                          setSelectedAreaIndex(params.dataIndex);
                          return;
                        }
                        if (params.componentType === 'yAxis' && typeof params.value === 'string') {
                          const idx = areaNames.findIndex(name => name === params.value);
                          if (idx >= 0) setSelectedAreaIndex(idx);
                        }
                      },
                      mousemove: (params: { dataIndex?: number; componentType?: string; value?: string }) => {
                        if (params.componentType === 'series' && typeof params.dataIndex === 'number') {
                          setHoverAreaIndex(params.dataIndex);
                          return;
                        }
                        if (params.componentType === 'yAxis' && typeof params.value === 'string') {
                          const idx = areaNames.findIndex(name => name === params.value);
                          setHoverAreaIndex(idx >= 0 ? idx : null);
                        }
                      },
                      globalout: () => setHoverAreaIndex(null),
                    }}
                  />
                </ResizeObserver>
              </Spin>
            </div>

            <div className={styles.card}>
              <Spin spinning={departmentDetailLoading}>
                <ResizeObserver
                  onResize={({ width, height }) => {
                    resizeTrendChart(departmentDetailChartRef, width, height);
                  }}
                >
                  <EchartsWithTheme
                    ref={e => {
                      if (e) departmentDetailChartRef.current = e;
                    }}
                    option={stackedOption}
                    style={trendChartStyle}
                    theme={theme}
                    empty={{
                      isShowEmpty: !hasDepartmentDetailData,
                      emptyNode: chartEmptyNode,
                    }}
                  />
                </ResizeObserver>
              </Spin>
            </div>
          </div>
        </div>
        <div className={styles.splitLine}></div>
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <img src={titleIcon} alt="" />
            <span>各类型瓶装气体出库趋势</span>
          </div>
          <div className={styles.grid}>
            <div className={styles.card}>
              <Spin spinning={loading}>
                <ResizeObserver
                  onResize={({ width, height }) => {
                    resizeTrendChart(gasTypeChartRef, width, height);
                  }}
                >
                  <EchartsWithTheme
                    ref={e => {
                      if (e) gasTypeChartRef.current = e;
                    }}
                    option={gasTypeOption}
                    style={trendChartStyle}
                    theme={theme}
                    empty={{
                      isShowEmpty: !hasGasTypeData,
                      emptyNode: chartEmptyNode,
                    }}
                    onEvents={{
                      click: (params: { dataIndex?: number; componentType?: string; value?: string }) => {
                        if (params.componentType === 'series' && typeof params.dataIndex === 'number') {
                          setSelectedGasTypeIndex(params.dataIndex);
                          return;
                        }
                        if (params.componentType === 'yAxis' && typeof params.value === 'string') {
                          const idx = gasTypes.findIndex(name => name === params.value);
                          if (idx >= 0) setSelectedGasTypeIndex(idx);
                        }
                      },
                      mousemove: (params: { dataIndex?: number; componentType?: string; value?: string }) => {
                        if (params.componentType === 'series' && typeof params.dataIndex === 'number') {
                          setHoverGasTypeIndex(params.dataIndex);
                          return;
                        }
                        if (params.componentType === 'yAxis' && typeof params.value === 'string') {
                          const idx = gasTypes.findIndex(name => name === params.value);
                          setHoverGasTypeIndex(idx >= 0 ? idx : null);
                        }
                      },
                      globalout: () => setHoverGasTypeIndex(null),
                    }}
                  />
                </ResizeObserver>
              </Spin>
            </div>

            <div className={styles.card}>
              <Spin spinning={gasMediumDetailLoading}>
                <ResizeObserver
                  onResize={({ width, height }) => {
                    resizeTrendChart(gasMediumDetailChartRef, width, height);
                  }}
                >
                  <EchartsWithTheme
                    ref={e => {
                      if (e) gasMediumDetailChartRef.current = e;
                    }}
                    option={singleGasOption}
                    style={trendChartStyle}
                    theme={theme}
                    empty={{
                      isShowEmpty: !hasGasMediumDetailData,
                      emptyNode: chartEmptyNode,
                    }}
                  />
                </ResizeObserver>
              </Spin>
            </div>
          </div>
        </div>
      </Wrapper>
      {outboundVisible && (
        <OutboundDetailsModal
          visible={outboundVisible}
          onClose={() => setOutboundVisible(false)}
          dateRange={requestRange}
        />
      )}
    </div>
  );
};

export default BottledGasStatistics;
